import { Suspense } from 'react'
import { Box, Loader } from '@mantine/core'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from '@/lib/auth-context'
import { OnShiftUsersProvider } from '@/lib/on-shift-users-context'
import { ThemeProvider } from '@/lib/theme-context'
import { MantineAppProvider } from '@/components/providers/MantineAppProvider'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { TicketsPage } from '@/pages/TicketsPage'
import { BookmarksPage } from '@/pages/BookmarksPage'
import { FiltersPage } from '@/pages/FiltersPage'
import { AnalyticsPage } from '@/pages/AnalyticsPage'
import { AnalyticsNewPage } from '@/pages/AnalyticsNewPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { SchedulesPage } from '@/pages/schedules/SchedulesPage'
import { ProjectAiAssistantPage } from '@/pages/ai-assistant/ProjectAiAssistantPage'
import { ActualizationPage } from '@/pages/ActualizationPage'
import { ActivityLogPage } from '@/pages/ActivityLogPage'
import { InstallersPage } from '@/pages/InstallersPage'
import { EmergencyModePage } from '@/pages/EmergencyModePage'
import { ReviewsPage } from '@/pages/ReviewsPage'
import { QualityArmPage } from '@/pages/QualityArmPage'
import { TicketDetailPage } from '@/pages/TicketDetailPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { DebugPage } from '@/pages/DebugPage'
import { ExportsPage } from '@/pages/ExportsPage'
import { UsersPage } from '@/pages/UsersPage'
import { OperatorsPage } from '@/pages/OperatorsPage'
import { AnomaliesPage } from '@/pages/AnomaliesPage'
import { MacrosPage } from '@/pages/MacrosPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { KnowledgeBasePage } from '@/pages/KnowledgeBasePage'
import { OperatorQuickSettingsPage } from '@/pages/operator/OperatorQuickSettingsPage'

function RouteLoader() {
  return (
    <Box style={{ minHeight: '40vh', display: 'grid', placeItems: 'center' }}>
      <Loader size="sm" />
    </Box>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <MantineAppProvider>
        <AuthProvider>
          <OnShiftUsersProvider>
            <BrowserRouter>
              <Box
                component="div"
                style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0 }}
              >
                <Suspense fallback={<RouteLoader />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route element={<RequireAuth />}>
                      <Route element={<AppLayout />}>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="tickets" element={<TicketsPage />} />
                        <Route path="tickets/:ticketRef" element={<TicketDetailPage />} />
                        <Route path="ai-tickets" element={<TicketsPage />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="analytics" element={<AnalyticsPage />} />
                        <Route path="analytics-new" element={<AnalyticsNewPage />} />
                        <Route path="bookmarks" element={<BookmarksPage />} />
                        <Route path="filters" element={<FiltersPage />} />
                        <Route path="reviews" element={<ReviewsPage />} />
                        <Route path="quality" element={<QualityArmPage />} />
                        <Route
                          path="failed-deliveries"
                          element={<Navigate to="/debug?tab=failed-deliveries" replace />}
                        />
                        <Route path="debug" element={<DebugPage />} />
                        <Route path="exports" element={<ExportsPage />} />
                        <Route path="installers" element={<InstallersPage />} />
                        <Route path="users" element={<UsersPage />} />
                        <Route path="operators" element={<OperatorsPage />} />
                        <Route path="operator/quick-settings" element={<OperatorQuickSettingsPage />} />
                        <Route
                          path="schedules/staffing-analysis"
                          element={<Navigate to="/schedules?tab=analysis" replace />}
                        />
                        <Route path="schedules" element={<SchedulesPage />} />
                        <Route path="emergency-mode" element={<EmergencyModePage />} />
                        <Route path="macros" element={<MacrosPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="settings/roles" element={<Navigate to="/settings?tab=roles" replace />} />
                        <Route path="rbac" element={<Navigate to="/settings?tab=roles" replace />} />
                        <Route path="roles" element={<Navigate to="/settings?tab=roles" replace />} />
                        <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                        <Route path="anomalies" element={<AnomaliesPage />} />
                        <Route path="actualization" element={<ActualizationPage />} />
                        <Route path="activity-log" element={<ActivityLogPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="ai-audit-log" element={<Navigate to="/debug?tab=ai-audit" replace />} />
                        <Route path="projects/:projectId/ai-assistant" element={<ProjectAiAssistantPage />} />
                      </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Box>
            </BrowserRouter>
          </OnShiftUsersProvider>
        </AuthProvider>
      </MantineAppProvider>
    </ThemeProvider>
  )
}
